'use client'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { getBenchmarkTabs } from '@/lib/benchmark-tabs'
import { useDataSetting } from '@/lib/data-setting-context'
import { useI18n } from '@/lib/i18n'
import { useTRPC } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import {
  getAllCompanies,
  getBenchmarkModels,
  getBenchmarkRankingModels,
  getCompanyDisplayName,
  getCompanyModels,
  getModelDisplayName,
} from '@repo/sdk'
import { useQuery } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, ChevronDown } from 'lucide-react'
import { type CSSProperties, useEffect, useMemo, useState } from 'react'
import { Tabs, TabsList, TabsTrigger } from './ui/tabs'

function getModelLogoPaths(modelName: string): { light: string, dark: string } | null {
  const company = modelName.split('/')[0]?.toLowerCase()

  switch (company) {
    case 'openai': {
      return {
        light: '/asset/company_logos/openai/openai-black.png',
        dark: '/asset/company_logos/openai/openai-white.png',
      }
    }
    case 'google': {
      return {
        light: '/asset/company_logos/google/gemini-color.png',
        dark: '/asset/company_logos/google/gemini-color.png',
      }
    }
    case 'anthropic': {
      return {
        light: '/asset/company_logos/anthropic/claude-color.png',
        dark: '/asset/company_logos/anthropic/claude-color.png',
      }
    }
    case 'x-ai': {
      return {
        light: '/asset/company_logos/x-ai/grok-black.png',
        dark: '/asset/company_logos/x-ai/grok-white.png',
      }
    }
    case 'z-ai': {
      return {
        light: '/asset/company_logos/z-ai/zai-black.png',
        dark: '/asset/company_logos/z-ai/zai-white.png',
      }
    }
    case 'deepseek': {
      return {
        light: '/asset/company_logos/deepseek/deepseek-color.png',
        dark: '/asset/company_logos/deepseek/deepseek-color.png',
      }
    }
    case 'moonshotai': {
      return {
        light: '/asset/company_logos/moonshot/kimi-black.png',
        dark: '/asset/company_logos/moonshot/kimi-white.png',
      }
    }
    default: {
      return null
    }
  }
}

function getCompanyLogoPaths(companyKey: string): { light: string, dark: string } | null {
  switch (companyKey.toLowerCase()) {
    case 'openai': {
      return {
        light: '/asset/company_logos/openai/openai-black.png',
        dark: '/asset/company_logos/openai/openai-white.png',
      }
    }
    case 'google': {
      return {
        light: '/asset/company_logos/google/gemini-color.png',
        dark: '/asset/company_logos/google/gemini-color.png',
      }
    }
    case 'anthropic': {
      return {
        light: '/asset/company_logos/anthropic/claude-color.png',
        dark: '/asset/company_logos/anthropic/claude-color.png',
      }
    }
    case 'x-ai': {
      return {
        light: '/asset/company_logos/x-ai/grok-black.png',
        dark: '/asset/company_logos/x-ai/grok-white.png',
      }
    }
    case 'z-ai': {
      return {
        light: '/asset/company_logos/z-ai/zai-black.png',
        dark: '/asset/company_logos/z-ai/zai-white.png',
      }
    }
    case 'deepseek': {
      return {
        light: '/asset/company_logos/deepseek/deepseek-color.png',
        dark: '/asset/company_logos/deepseek/deepseek-color.png',
      }
    }
    case 'moonshotai': {
      return {
        light: '/asset/company_logos/moonshot/kimi-black.png',
        dark: '/asset/company_logos/moonshot/kimi-white.png',
      }
    }
    default: {
      return null
    }
  }
}

function LogoImage({
  modelName,
  size = 20,
  className,
}: {
  modelName: string
  size?: number
  className?: string
}) {
  const logoPaths = getModelLogoPaths(modelName)

  if (!logoPaths)
    return null

  return (
    <div
      className={cn('mr-2 inline-block rounded', className)}
      style={{ width: size, height: size }}
    >
      <img
        src={logoPaths.light}
        alt={`${modelName} logo`}
        className="size-full object-contain dark:hidden"
      />
      <img
        src={logoPaths.dark}
        alt={`${modelName} logo`}
        className="hidden size-full object-contain dark:block"
      />
    </div>
  )
}

function CompanyLogoImage({ companyKey, size = 16 }: { companyKey: string, size?: number }) {
  const logoPaths = getCompanyLogoPaths(companyKey)

  if (!logoPaths)
    return null

  return (
    <div className="mr-1 inline-block rounded" style={{ width: size, height: size }}>
      <img
        src={logoPaths.light}
        alt={`${companyKey} logo`}
        className="size-full object-contain dark:hidden"
      />
      <img
        src={logoPaths.dark}
        alt={`${companyKey} logo`}
        className="hidden size-full object-contain dark:block"
      />
    </div>
  )
}

function HorizontalBarChart({ data }: { data: ModelStatsData[] }) {
  const topFive = [...data]
    .sort((a, b) => b.winRate - a.winRate)
    .slice(0, 5)

  return (
    <div className="space-y-6 sm:px-4">
      {topFive.map((item, index) => (
        <div key={index} className="flex items-center gap-2 sm:gap-4">
          { }
          <div className="flex w-7 flex-shrink-0 items-center justify-center sm:w-8">
            <span className="text-base font-semibold text-foreground">
              #{index + 1}
            </span>
          </div>
          { }
          <div className="flex w-[7.5rem] flex-shrink-0 items-start sm:w-[240px]">
            <div className="mt-0.5 flex-shrink-0">
              <LogoImage modelName={item.model} size={20} />
            </div>
            <span className="ml-2 break-words text-base font-medium leading-tight text-foreground">
              {getModelDisplayName(item.model)}
            </span>
          </div>

          { }
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:ml-8 sm:gap-3">
            <div className="relative h-6 flex-1 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-gradient-to-r from-gray-300 to-gray-700 transition-all duration-500 ease-out dark:from-gray-400 dark:to-gray-200"
                style={{
                  width: `${item.winRate}%`,
                  minWidth: item.winRate > 0 ? '4px' : '0px',
                }}
              />
            </div>

            { }
            <div className="min-w-12 text-right sm:min-w-[60px]">
              <span className="text-sm font-semibold text-foreground">
                {item.winRate.toFixed(1)}%
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

interface FieldDefinition {
  fieldKey: string
  displayName: string
}

type ModelActionStats = Record<
  string,
  {
    totalActions: number
    preciseActions: number
    preciseRate: number
  }
>

interface ModelStatsData {
  model: string
  totalGames: number
  winRate: number
}

interface CompanyModel {
  key: string
  displayName: string
  models: string[]
}

interface ModelComparisonData {
  wins: number
  total: number
}

interface CompanyWinRateStats {
  model: string
  totalGames: number
  wins: number
  winRate: number
}

type CompanyComparisonData = Record<string, Record<string, ModelComparisonData>>
type CompanyWinRateData = Record<string, CompanyWinRateStats[]>

function getCompaniesFromSDK(): CompanyModel[] {
  return getAllCompanies().map((companyKey: string) => ({
    key: companyKey,
    displayName: getCompanyDisplayName(companyKey),
    models: getCompanyModels(companyKey),
  }))
}

function transformCompanyData(
  companyData: Record<string, unknown> | undefined,
): Record<string, CompanyComparisonData> {
  if (!companyData) {
    return {}
  }

  const result: Record<string, CompanyComparisonData> = {}

  for (const companyKey of Object.keys(companyData)) {
    const displayName = getCompanyDisplayName(companyKey)
    const data = companyData[companyKey] as {
      modelPairsGameStats?: Record<
        string,
        Record<string, { a: number, c: number }>
      >
    }

    if (!data?.modelPairsGameStats) {
      result[displayName] = {}
      continue
    }

    const companyResult: CompanyComparisonData = {}
    const modelPairs = data.modelPairsGameStats

    for (const goodModel of Object.keys(modelPairs)) {
      const goodModelDisplay = getModelDisplayName(goodModel)
      companyResult[goodModelDisplay] = {}

      for (const evilModel of Object.keys(modelPairs[goodModel] || {})) {
        const evilModelDisplay = getModelDisplayName(evilModel)
        const stats = modelPairs[goodModel]?.[evilModel]

        if (stats) {
          companyResult[goodModelDisplay][evilModelDisplay] = {
            wins: stats.a,
            total: stats.c,
          }
        }
      }
    }

    result[displayName] = companyResult
  }

  return result
}

function transformCompanyWinRateData(
  companyData: Record<string, unknown> | undefined,
): CompanyWinRateData {
  if (!companyData) {
    return {}
  }

  const result: CompanyWinRateData = {}

  for (const companyKey of Object.keys(companyData)) {
    const displayName = getCompanyDisplayName(companyKey)
    const data = companyData[companyKey] as {
      modelOverallWinningRate?: {
        modelStats?: Array<{
          model: string
          totalGames: number
          wins: number
          winRate: number
        }>
      }
    }

    if (!data?.modelOverallWinningRate?.modelStats) {
      result[displayName] = []
      continue
    }

    result[displayName] = data.modelOverallWinningRate.modelStats
      .map(stat => ({
        model: getModelDisplayName(stat.model),
        totalGames: stat.totalGames,
        wins: stat.wins,
        winRate: stat.winRate,
      }))
      .sort((a, b) => b.winRate - a.winRate)
  }

  return result
}

function GameSettingTabs() {
  const { selectedDataSetting, setSelectedDataSetting } = useDataSetting()
  const { t } = useI18n()
  const trpc = useTRPC()

  const { data: allGameStatsResponse }
    = useQuery(trpc.queryRouter.getAllGameStats.queryOptions())

  const gameSettings = useMemo(() =>
    allGameStatsResponse?.success
      ? getBenchmarkTabs(Object.keys(allGameStatsResponse.data))
      : [], [allGameStatsResponse])

  return (
    <Tabs
      value={selectedDataSetting}
      onValueChange={setSelectedDataSetting}
      className="mb-0"
    >
      <TabsList className="relative mb-0 grid h-auto min-h-9 w-full grid-cols-3 gap-2 overflow-visible rounded-none border-b border-border/50 bg-transparent p-0">
        {gameSettings.map(gameSetting => (
          <TabsTrigger
            key={gameSetting}
            value={gameSetting}
            className={cn(
              'relative z-10 h-full min-w-0 whitespace-normal px-2 py-1 leading-tight transition-all duration-300 ease-out',
              'data-[state=active]:overflow-hidden',

              'data-[state=inactive]:rounded-lg data-[state=inactive]:text-sm data-[state=inactive]:font-medium data-[state=inactive]:text-black data-[state=inactive]:hover:bg-muted/5 data-[state=inactive]:hover:text-foreground/80 dark:data-[state=inactive]:text-white',

              'data-[state=active]:z-20 data-[state=active]:translate-y-[-2px]',
              'data-[state=active]:rounded-lg',

              'data-[state=active]:border-x data-[state=active]:border-t-2 data-[state=active]:border-amber-400/35 data-[state=active]:bg-gradient-to-br data-[state=active]:from-amber-300 data-[state=active]:via-yellow-400 data-[state=active]:to-amber-500 data-[state=active]:text-amber-950 data-[state=active]:shadow-lg data-[state=active]:shadow-amber-500/55',
              'data-[state=active]:before:absolute data-[state=active]:before:inset-0 data-[state=active]:before:bg-gradient-to-br data-[state=active]:before:from-white/45 data-[state=active]:before:via-amber-300/25 data-[state=active]:before:to-transparent data-[state=active]:before:opacity-65',
              'data-[state=active]:after:absolute data-[state=active]:after:inset-0 data-[state=active]:after:bg-gradient-to-tl data-[state=active]:after:from-yellow-300/30 data-[state=active]:after:via-transparent data-[state=active]:after:to-white/25 data-[state=active]:after:opacity-55',

              'dark:data-[state=active]:border-amber-500/35 dark:data-[state=active]:from-amber-400 dark:data-[state=active]:via-yellow-500 dark:data-[state=active]:to-amber-600 dark:data-[state=active]:text-black dark:data-[state=active]:shadow-amber-500/55',

              'data-[state=active]:text-sm data-[state=active]:font-bold',
            )}
          >
            <span className="relative z-10">
              {t(`gameSettings.${gameSetting}`)}
            </span>
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  )
}

function AnalysisReportSection() {
  const [isMounted, setIsMounted] = useState(false)
  const [isModelPairsExpanded, setIsModelPairsExpanded] = useState(false)
  const [isPreciseRateExpanded, setIsPreciseRateExpanded] = useState(false)
  const { t } = useI18n()
  const { selectedDataSetting } = useDataSetting()
  const trpc = useTRPC()

  const companies = useMemo(() => getCompaniesFromSDK(), [])
  const [selectedCompany, setSelectedCompany] = useState(
    companies.find(c => c.key.toLowerCase().includes('openai'))?.displayName
    || companies[0]?.displayName
    || '',
  )

  const [preciseRateSort, setPreciseRateSort] = useState<{
    column: string
    direction: 'asc' | 'desc'
  }>({ column: 'rank', direction: 'asc' })

  const [comparisonModel1, setComparisonModel1] = useState<string>('all')
  const [comparisonModel2, setComparisonModel2] = useState<string>('all')

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    setComparisonModel1('all')
    setComparisonModel2('all')
  }, [selectedDataSetting])

  const {
    data: allGameStatsResponse,
    isLoading: isLoadingAllStats,
    error: allStatsError,
  } = useQuery({
    ...trpc.queryRouter.getAllGameStats.queryOptions(),
    enabled: isMounted,
  })

  const allGameStats = useMemo(() => allGameStatsResponse?.success
    ? allGameStatsResponse.data
    : ({} as Record<string, Record<string, unknown>>), [allGameStatsResponse])

  const currentGameSettingData = useMemo(() => (allGameStats[selectedDataSetting]
    || {}) as Record<string, unknown>, [allGameStats, selectedDataSetting])

  const benchmarkModels = useMemo(
    () => getBenchmarkModels(selectedDataSetting),
    [selectedDataSetting],
  )
  const rankingModels = useMemo(
    () => getBenchmarkRankingModels(selectedDataSetting),
    [selectedDataSetting],
  )

  const modelStatsData = useMemo(() => {
    const stats = ((currentGameSettingData.modelOverallWinningRate as
      | { modelStats?: ModelStatsData[] }
      | undefined)?.modelStats || []) as ModelStatsData[]
    if (rankingModels.length === 0) {
      return stats
    }
    const rankingSet = new Set(rankingModels)
    return stats.filter(stat => rankingSet.has(stat.model))
  }, [currentGameSettingData, rankingModels])
  const lastUpdated = allGameStatsResponse?.lastUpdated || ''

  const modelPairsStats = useMemo(
    () =>
      (currentGameSettingData.modelPairsGameStats || {}) as Record<
        string,
        Record<string, { a: number, b: number, c: number, f: number }>
      >,
    [currentGameSettingData],
  )
  const modelPairsLastUpdated = allGameStatsResponse?.lastUpdated || ''

  const preciseRateData = useMemo(() =>
    ((currentGameSettingData.modelActionPreciseRate as
    | { modelStats?: Record<string, unknown> }
    | undefined)?.modelStats || {}) as Record<string, unknown>, [currentGameSettingData])

  const fieldDefinitions = useMemo(() =>
    (((currentGameSettingData.modelActionPreciseRate as
    | { fieldDefinitions?: FieldDefinition[] }
    | undefined)?.fieldDefinitions || []) as FieldDefinition[]).sort((a, b) => {
      const order = [
        'witchPoisonPreciseRate',
        'seerPreciseRate',
        'hunterPreciseRate',
        'villagerVotePreciseRate',
        'werewolfElimiateLastVillagerPreciseRate',
        'werewolfMultiVillagerEliminateGodPreciseRate',
      ]
      const indexA = order.indexOf(a.fieldKey)
      const indexB = order.indexOf(b.fieldKey)

      if (indexA === -1 && indexB === -1)
        return 0
      if (indexA === -1)
        return 1
      if (indexB === -1)
        return -1
      return indexA - indexB
    }), [currentGameSettingData])
  const preciseRateLastUpdated = allGameStatsResponse?.lastUpdated || ''

  const overallWinRateStatsByModel = useMemo(
    () =>
      Object.fromEntries(
        modelStatsData.map(m => [
          m.model,
          {
            wins: Math.round((m.totalGames * m.winRate) / 100),
            total: m.totalGames,
            rate: m.winRate,
          },
        ]),
      ) as Record<string, { wins: number, total: number, rate: number }>,
    [modelStatsData],
  )

  const winRateRankByModel = useMemo(() => {
    const sorted = [...modelStatsData].sort((a, b) => b.winRate - a.winRate)
    return Object.fromEntries(
      sorted.map((m, i) => [m.model, i + 1]),
    ) as Record<string, number>
  }, [modelStatsData])

  const sortedPreciseRateEntries = useMemo(() => {
    const entries = Object.entries(preciseRateData) as Array<[string, ModelActionStats]>
    const { column, direction } = preciseRateSort
    const mult = direction === 'asc' ? 1 : -1

    return [...entries].sort(([modelA, statsA], [modelB, statsB]) => {
      let aVal: string | number
      let bVal: string | number
      if (column === 'aiModel') {
        aVal = (modelA.split('/').pop() || modelA).toLowerCase()
        bVal = (modelB.split('/').pop() || modelB).toLowerCase()
        return mult * (aVal < bVal ? -1 : aVal > bVal ? 1 : 0)
      }
      if (column === 'rank') {
        aVal = winRateRankByModel[modelA] ?? 9999
        bVal = winRateRankByModel[modelB] ?? 9999
        return mult * (aVal - bVal)
      }
      if (column === 'winRate') {
        aVal = overallWinRateStatsByModel[modelA]?.rate ?? -1
        bVal = overallWinRateStatsByModel[modelB]?.rate ?? -1
        return mult * (aVal - bVal)
      }
      const rateA = statsA[column]?.preciseRate ?? -1
      const rateB = statsB[column]?.preciseRate ?? -1
      return mult * (rateA - rateB)
    })
  }, [
    preciseRateData,
    preciseRateSort,
    winRateRankByModel,
    overallWinRateStatsByModel,
  ])

  const handlePreciseRateSort = (column: string) => {
    setPreciseRateSort(prev =>
      prev.column === column
        ? { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { column, direction: 'asc' },
    )
  }

  const companyData = allGameStatsResponse?.companyData as
    | Record<string, unknown>
    | undefined
  const comparisonData = useMemo(
    () => transformCompanyData(companyData),
    [companyData],
  )

  const companyWinRateData = useMemo(
    () => transformCompanyWinRateData(companyData),
    [companyData],
  )

  const comparisonStats = useMemo(() => {
    if (comparisonModel1 === 'all' || comparisonModel2 === 'all')
      return null

    const model1 = comparisonModel1
    const model2 = comparisonModel2

    const stats1v2 = modelPairsStats[model1]?.[model2]
    const stats2v1 = modelPairsStats[model2]?.[model1]

    let winRate1Good = 0
    let winRate1Evil = 0

    if (!(stats1v2 && stats1v2.c > 0 && stats1v2.a !== -1)) {
      return null
    }
    winRate1Good = stats1v2.a / stats1v2.c

    if (!(stats2v1 && stats2v1.c > 0 && stats2v1.a !== -1)) {
      return null
    }
    const winRate2Good = stats2v1.a / stats2v1.c
    winRate1Evil = 1 - winRate2Good

    const normalizedScore1 = (winRate1Good + winRate1Evil) / 2
    const normalizedScore2 = 1 - normalizedScore1

    return {
      model1: { name: model1, score: normalizedScore1 },
      model2: { name: model2, score: normalizedScore2 },
    }
  }, [comparisonModel1, comparisonModel2, modelPairsStats])

  const isSingleModelSelection = useMemo(() => (
    (comparisonModel1 === 'all' && comparisonModel2 !== 'all')
    || (comparisonModel2 === 'all' && comparisonModel1 !== 'all')
  ), [comparisonModel1, comparisonModel2])

  const singleSelectedModel = useMemo(() => {
    if (!isSingleModelSelection) {
      return null
    }
    return comparisonModel1 === 'all' ? comparisonModel2 : comparisonModel1
  }, [comparisonModel1, comparisonModel2, isSingleModelSelection])

  if (allStatsError) {
    console.error('Error fetching all game stats:', allStatsError)
  }

  return (
    <div>
      { }
      <div className="min-h-dvh bg-section-light px-4 py-20 sm:px-6 lg:px-8 lg:pt-20">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-2xl font-bold text-foreground md:text-3xl lg:text-4xl">
              {t('leaderboard.title')}
            </h2>
            <p className="mx-auto max-w-4xl text-lg text-muted-foreground">
              {t('leaderboard.gamePerformanceDescription')}
            </p>
          </div>

          <div className="grid grid-cols-[minmax(0,1fr)] gap-8">
            { }
            <div>
              <GameSettingTabs />
              <Card className="rounded-t-none border-t-0 shadow-none lg:rounded-t-none lg:border-t-0">
                <CardHeader>
                  <CardTitle className="text-left text-xl font-semibold">
                    {t('whatWeDo.playerRankingTitle')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoadingAllStats ? (
                    <div className="py-8 text-center">
                      <p className="text-muted-foreground">
                        {t('leaderboard.loadingData')}
                      </p>
                    </div>
                  ) : modelStatsData.length === 0 ? (
                    <div className="py-8 text-center">
                      <p className="text-muted-foreground">
                        {t('leaderboard.noDataAvailable')}
                      </p>
                    </div>
                  ) : (
                    <>
                      <HorizontalBarChart data={modelStatsData} />
                      {lastUpdated && (
                        <div className="mt-6 text-center">
                          <p className="text-xs text-muted-foreground">
                            {t('whatWeDo.lastUpdated')}:{' '}
                            {new Date(lastUpdated).toLocaleString()}
                          </p>
                        </div>
                      )}

                      <div className="mt-6 space-y-3">
                        <div className="rounded-lg border border-muted bg-muted/30 p-4">
                          <div className="grid gap-2 text-sm text-muted-foreground">
                            { }
                            <div
                              dangerouslySetInnerHTML={{
                                __html: t('leaderboard.winRateExplanations'),
                              }}
                              className="space-y-1"
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            { }
            <div>
              <button
                type="button"
                onClick={() => setIsModelPairsExpanded(prev => !prev)}
                aria-expanded={isModelPairsExpanded}
                className="flex w-full items-center justify-between rounded-lg border bg-card px-6 py-5 text-left shadow-none transition-colors hover:bg-muted/20"
              >
                <span className="text-xl font-semibold">
                  {t('leaderboard.modelPairsMatrix')}
                </span>
                <ChevronDown
                  className={cn(
                    'size-5 text-muted-foreground transition-transform',
                    isModelPairsExpanded && 'rotate-180',
                  )}
                />
              </button>

              {isModelPairsExpanded && (
                <div className="mt-4">
                  {isLoadingAllStats ? (
                    <div className="py-8 text-center">
                      <p className="text-muted-foreground">
                        {t('leaderboard.loadingData')}
                      </p>
                    </div>
                  ) : Object.keys(modelPairsStats).length === 0 ? (
                    <div className="py-8 text-center">
                      <p className="text-muted-foreground">
                        {t('leaderboard.noDataAvailable')}
                      </p>
                    </div>
                  ) : (
                    <div>
                      <GameSettingTabs />
                      <Card className="rounded-t-none border-t-0 shadow-none lg:rounded-t-none lg:border-t-0">
                        <CardContent className="pt-6">
                          <div className="mb-6 rounded-lg border border-border bg-gradient-to-br from-muted/30 to-muted/10 p-6">
                            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                              <div className="flex flex-col items-center gap-2">
                                <Label className="text-sm font-medium text-muted-foreground">{t('leaderboard.modelA')}</Label>
                                <Select value={comparisonModel1} onValueChange={setComparisonModel1}>
                                  <SelectTrigger className="w-[200px]">
                                    <SelectValue placeholder={t('leaderboard.allModels')} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="all">{t('leaderboard.allModels')}</SelectItem>
                                    {getBenchmarkModels(selectedDataSetting).map((model: string) => (
                                      <SelectItem key={model} value={model}>
                                        {getModelDisplayName(model)}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="flex flex-col items-center gap-2">
                                <Label className="text-sm font-medium text-muted-foreground">{t('leaderboard.modelB')}</Label>
                                <Select value={comparisonModel2} onValueChange={setComparisonModel2}>
                                  <SelectTrigger className="w-[200px]">
                                    <SelectValue placeholder={t('leaderboard.allModels')} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="all">{t('leaderboard.allModels')}</SelectItem>
                                    {getBenchmarkModels(selectedDataSetting).map((model: string) => (
                                      <SelectItem key={model} value={model}>
                                        {getModelDisplayName(model)}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            {comparisonStats && (
                              <>
                                <div className="my-6 border-t border-border/50" />
                                <div>
                                  <div className="flex flex-col items-center justify-center gap-6 sm:flex-row sm:gap-12">
                                    { }
                                    <div className={cn(
                                      'relative flex min-w-[140px] flex-col items-center rounded-xl border p-4 transition-all',
                                      comparisonStats.model1.score > comparisonStats.model2.score
                                        ? 'border-amber-400/50 bg-amber-400/10 shadow-[0_0_15px_-3px_rgba(251,191,36,0.2)] dark:bg-amber-400/5'
                                        : 'border-transparent bg-muted/50',
                                    )}
                                    >
                                      {comparisonStats.model1.score > comparisonStats.model2.score && (
                                        <div className="absolute -top-3 rounded-full bg-amber-400 px-2 py-0.5 text-xs font-bold text-amber-950 shadow-sm">
                                          {t('leaderboard.winner') || 'Winner'}
                                        </div>
                                      )}
                                      <LogoImage modelName={comparisonStats.model1.name} size={48} className="!mr-0 mb-3" />
                                      <div className="mb-1 flex h-10 items-center justify-center">
                                        <span className="text-center font-medium leading-tight">
                                          {getModelDisplayName(comparisonStats.model1.name)}
                                        </span>
                                      </div>
                                      <span className={cn(
                                        'text-2xl font-bold',
                                        comparisonStats.model1.score > comparisonStats.model2.score ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground',
                                      )}
                                      >
                                        {(comparisonStats.model1.score * 100).toFixed(1)}%
                                      </span>
                                    </div>

                                    <div className="text-xl font-bold text-muted-foreground/30">VS</div>

                                    { }
                                    <div className={cn(
                                      'relative flex min-w-[140px] flex-col items-center rounded-xl border p-4 transition-all',
                                      comparisonStats.model2.score > comparisonStats.model1.score
                                        ? 'border-amber-400/50 bg-amber-400/10 shadow-[0_0_15px_-3px_rgba(251,191,36,0.2)] dark:bg-amber-400/5'
                                        : 'border-transparent bg-muted/50',
                                    )}
                                    >
                                      {comparisonStats.model2.score > comparisonStats.model1.score && (
                                        <div className="absolute -top-3 rounded-full bg-amber-400 px-2 py-0.5 text-xs font-bold text-amber-950 shadow-sm">
                                          {t('leaderboard.winner') || 'Winner'}
                                        </div>
                                      )}
                                      <LogoImage modelName={comparisonStats.model2.name} size={48} className="!mr-0 mb-3" />
                                      <div className="mb-1 flex h-10 items-center justify-center">
                                        <span className="text-center font-medium leading-tight">
                                          {getModelDisplayName(comparisonStats.model2.name)}
                                        </span>
                                      </div>
                                      <span className={cn(
                                        'text-2xl font-bold',
                                        comparisonStats.model2.score > comparisonStats.model1.score ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground',
                                      )}
                                      >
                                        {(comparisonStats.model2.score * 100).toFixed(1)}%
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>

                          <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                              <thead>
                                <tr className="border-b border-border">
                                  <th className="min-w-[200px] px-4 py-3 text-left text-base font-semibold text-foreground">
                                    {t('leaderboard.goodModelVsEvilModel')}
                                  </th>
                                  {(comparisonModel1 !== 'all' && comparisonModel2 !== 'all'
                                    ? benchmarkModels.filter(
                                        model =>
                                          model === comparisonModel1
                                          || model === comparisonModel2,
                                      )
                                    : benchmarkModels
                                  ).map(
                                    (evilModel: string) => (
                                      <th
                                        key={evilModel}
                                        className={cn(
                                          'min-w-[120px] px-4 py-3 text-center text-base font-semibold text-foreground transition-colors',
                                          isSingleModelSelection
                                          && evilModel === singleSelectedModel
                                          && 'bg-amber-400/20',
                                        )}
                                        title={evilModel}
                                      >
                                        <div className="flex flex-col items-center">
                                          <LogoImage modelName={evilModel} size={16} />
                                          <span>
                                            {evilModel
                                              .split('/')
                                              .pop()
                                              ?.substring(0, 20)
                                              || evilModel.substring(0, 20)}
                                            {(evilModel.split('/').pop()?.length
                                              || evilModel.length) > 20
                                              ? '...'
                                              : ''}
                                          </span>
                                        </div>
                                      </th>
                                    ),
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {(comparisonModel1 !== 'all' && comparisonModel2 !== 'all'
                                  ? benchmarkModels.filter(
                                      model =>
                                        model === comparisonModel1
                                        || model === comparisonModel2,
                                    )
                                  : benchmarkModels
                                ).map(
                                  (goodModel: string) => (
                                    <tr
                                      key={goodModel}
                                      className={cn(
                                        'border-b border-border/50 transition-colors hover:bg-muted/50',
                                        isSingleModelSelection
                                        && goodModel === singleSelectedModel
                                        && 'bg-amber-400/10',
                                      )}
                                    >
                                      <td
                                        className="min-w-[200px] px-4 py-3 text-left font-medium text-foreground transition-colors"
                                        title={goodModel}
                                      >
                                        <div className="flex items-center">
                                          <LogoImage modelName={goodModel} size={20} />
                                          <span>
                                            {goodModel
                                              .split('/')
                                              .pop()
                                              ?.substring(0, 20)
                                              || goodModel.substring(0, 20)}
                                            {(goodModel.split('/').pop()?.length
                                              || goodModel.length) > 20
                                              ? '...'
                                              : ''}
                                          </span>
                                        </div>
                                      </td>
                                      {(comparisonModel1 !== 'all' && comparisonModel2 !== 'all'
                                        ? benchmarkModels.filter(
                                            model =>
                                              model === comparisonModel1
                                              || model === comparisonModel2,
                                          )
                                        : benchmarkModels
                                      ).map(
                                        (evilModel: string) => {
                                          const gameStats = modelPairsStats[
                                            goodModel
                                          ]?.[evilModel] ?? {
                                            a: 0,
                                            c: 0,
                                          }
                                          const displayValue
                                            = gameStats.a === -1
                                              ? '-'
                                              : `${gameStats.a}/${gameStats.c}`

                                          const winRate = gameStats.c > 0 && gameStats.a !== -1
                                            ? ((gameStats.a / gameStats.c) * 100).toFixed(1)
                                            : null

                                          const isIntersection
                                            = goodModel !== evilModel
                                              && ((goodModel === comparisonModel1 && evilModel === comparisonModel2)
                                                || (goodModel === comparisonModel2 && evilModel === comparisonModel1))

                                          return (
                                            <td
                                              key={evilModel}
                                              className={cn(
                                                'px-4 py-3 text-center transition-colors',
                                                isIntersection && 'bg-amber-400/20',
                                                isSingleModelSelection
                                                && (goodModel === singleSelectedModel
                                                  || evilModel === singleSelectedModel)
                                                && 'bg-amber-400/10',
                                              )}
                                            >
                                              <div className="flex flex-col items-center">
                                                <span
                                                  className={`${gameStats.a === -1 ? 'text-muted-foreground' : 'text-foreground'}`}
                                                >
                                                  {displayValue}
                                                </span>
                                                {winRate && (
                                                  <span className="text-xs text-muted-foreground">
                                                    ({winRate}%)
                                                  </span>
                                                )}
                                              </div>
                                            </td>
                                          )
                                        },
                                      )}
                                    </tr>
                                  ),
                                )}
                              </tbody>
                            </table>
                          </div>
                          <div className="mt-6 space-y-3">
                            {modelPairsLastUpdated && (
                              <p className="text-center text-xs text-muted-foreground">
                                {t('leaderboard.lastUpdated')}:{' '}
                                {new Date(modelPairsLastUpdated).toLocaleString()}
                              </p>
                            )}

                            <div className="rounded-lg border border-muted bg-muted/30 p-4">
                              <div className="grid gap-2 text-sm text-muted-foreground">
                                <p>{t('leaderboard.modelPairsDescription')}</p>
                                <p>{t('leaderboard.diagonalElementsNote')}</p>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </div>
              )}
            </div>

            { }
            <div>
              <button
                type="button"
                onClick={() => setIsPreciseRateExpanded(prev => !prev)}
                aria-expanded={isPreciseRateExpanded}
                className="flex w-full items-center justify-between rounded-lg border bg-card px-6 py-5 text-left shadow-none transition-colors hover:bg-muted/20"
              >
                <span className="text-xl font-semibold">
                  {t('leaderboard.witchActionPreciseRate')}
                </span>
                <ChevronDown
                  className={cn(
                    'size-5 text-muted-foreground transition-transform',
                    isPreciseRateExpanded && 'rotate-180',
                  )}
                />
              </button>

              {isPreciseRateExpanded && (
                <div className="mt-4">
                  <GameSettingTabs />
                  <Card className="rounded-t-none border-t-0 shadow-none lg:rounded-t-none lg:border-t-0">
                    <CardContent className="pt-6">
                      {isLoadingAllStats ? (
                        <div className="py-8 text-center">
                          <p className="text-muted-foreground">
                            {t('leaderboard.loadingData')}
                          </p>
                        </div>
                      ) : Object.keys(preciseRateData).length === 0 ? (
                        <div className="py-8 text-center">
                          <p className="text-muted-foreground">
                            {t('leaderboard.noDataAvailable')}
                          </p>
                        </div>
                      ) : (
                        <>
                          <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                              <thead>
                                <tr className="border-b border-border">
                                  <th
                                    className="cursor-pointer px-4 py-3 text-left text-base font-semibold text-foreground hover:opacity-80"
                                    onClick={() => handlePreciseRateSort('aiModel')}
                                  >
                                    <span className="inline-flex items-center gap-1">
                                      {t('leaderboard.aiModel')}
                                      {preciseRateSort.column === 'aiModel'
                                        && (preciseRateSort.direction === 'asc' ? (
                                          <ArrowDown className="size-4" />
                                        ) : (
                                          <ArrowUp className="size-4" />
                                        ))}
                                    </span>
                                  </th>
                                  <th
                                    className="cursor-pointer px-4 py-3 text-center text-base font-semibold text-foreground hover:opacity-80"
                                    onClick={() => handlePreciseRateSort('rank')}
                                  >
                                    <span className="inline-flex items-center justify-center gap-1">
                                      {t('leaderboard.rank')}
                                      {preciseRateSort.column === 'rank'
                                        && (preciseRateSort.direction === 'asc' ? (
                                          <ArrowDown className="size-4" />
                                        ) : (
                                          <ArrowUp className="size-4" />
                                        ))}
                                    </span>
                                  </th>
                                  <th
                                    className="cursor-pointer px-4 py-3 text-center text-base font-semibold text-foreground hover:opacity-80"
                                    onClick={() => handlePreciseRateSort('winRate')}
                                  >
                                    <span className="inline-flex items-center justify-center gap-1">
                                      {t('whatWeDo.winRate')}
                                      {preciseRateSort.column === 'winRate'
                                        && (preciseRateSort.direction === 'asc' ? (
                                          <ArrowDown className="size-4" />
                                        ) : (
                                          <ArrowUp className="size-4" />
                                        ))}
                                    </span>
                                  </th>
                                  {fieldDefinitions.map(
                                    (field: FieldDefinition) => (
                                      <th
                                        key={field.fieldKey}
                                        className="cursor-pointer px-4 py-3 text-center text-base font-semibold text-foreground hover:opacity-80"
                                        onClick={() =>
                                          handlePreciseRateSort(field.fieldKey)}
                                      >
                                        <span className="inline-flex items-center justify-center gap-1">
                                          {t(field.displayName)}
                                          {preciseRateSort.column === field.fieldKey
                                            && (preciseRateSort.direction === 'asc' ? (
                                              <ArrowDown className="size-4" />
                                            ) : (
                                              <ArrowUp className="size-4" />
                                            ))}
                                        </span>
                                      </th>
                                    ),
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {sortedPreciseRateEntries.map(
                                  ([modelName, modelStats]) => (
                                    <tr
                                      key={modelName}
                                      className="border-b border-border/50 transition-colors hover:bg-muted/50"
                                    >
                                      <td className="px-4 py-3 text-left font-medium text-foreground">
                                        <div className="flex items-center gap-2">
                                          <LogoImage
                                            modelName={modelName}
                                            size={20}
                                            className="!mr-0 mt-px shrink-0"
                                          />
                                          <span>
                                            {modelName.split('/').pop()
                                              || modelName}
                                          </span>
                                        </div>
                                      </td>
                                      <td className="px-4 py-3 text-center">
                                        <span className="text-base font-semibold text-foreground">
                                          {winRateRankByModel[modelName] !== undefined
                                            ? `${winRateRankByModel[modelName]}`
                                            : '-'}
                                        </span>
                                      </td>
                                      <td className="px-4 py-3 text-center">
                                        {(() => {
                                          const stats = overallWinRateStatsByModel[modelName]
                                          if (!stats) {
                                            return (
                                              <span className="text-foreground">-</span>
                                            )
                                          }
                                          return (
                                            <div className="flex flex-col items-center gap-1">
                                              <span className="text-sm text-muted-foreground">
                                                ({stats.wins}, {stats.total})
                                              </span>
                                              <span className="text-foreground">
                                                {stats.rate.toFixed(1)}%
                                              </span>
                                            </div>
                                          )
                                        })()}
                                      </td>
                                      {fieldDefinitions.map(
                                        (field: FieldDefinition) => {
                                          const fieldStats = (
                                            modelStats as ModelActionStats
                                          )[field.fieldKey]
                                          return (
                                            <td
                                              key={field.fieldKey}
                                              className="px-4 py-3 text-center"
                                            >
                                              <div className="flex flex-col items-center gap-1">
                                                <span className="text-sm text-muted-foreground">
                                                  ({fieldStats?.preciseActions || 0}
                                                  , {fieldStats?.totalActions || 0})
                                                </span>
                                                <span className="text-foreground">
                                                  {(
                                                    fieldStats?.preciseRate || 0
                                                  ).toFixed(1)}
                                                  %
                                                </span>
                                              </div>
                                            </td>
                                          )
                                        },
                                      )}
                                    </tr>
                                  ),
                                )}
                              </tbody>
                            </table>
                          </div>
                          <div className="mt-6 space-y-3">
                            {preciseRateLastUpdated && (
                              <p className="text-center text-xs text-muted-foreground">
                                {t('leaderboard.lastUpdated')}:{' '}
                                {new Date(preciseRateLastUpdated).toLocaleString()}
                              </p>
                            )}

                            <div className="rounded-lg border border-muted bg-muted/30 p-4">
                              <div className="grid gap-2 text-sm text-muted-foreground">
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: t(
                                      'leaderboard.specialRoleAccuracyExplanations',
                                    ),
                                  }}
                                  className="space-y-1"
                                />
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      { }
      <div className="min-h-dvh bg-section-light px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          { }
          <div className="mb-4 text-center">
            <h2 className="mx-auto max-w-4xl text-2xl font-bold text-foreground">
              {t('arena.matrixTitle')}
            </h2>
          </div>

          { }
          <div className="mb-8 text-center">
            <p className="mx-auto max-w-4xl text-lg text-muted-foreground">
              {t('arena.matrixSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-[minmax(0,1fr)] gap-8">
            { }
            <div>
              <Tabs
                value={selectedCompany}
                onValueChange={setSelectedCompany}
                className="mb-0"
              >
                <TabsList
                  className="relative mb-0 grid h-auto min-h-9 w-full grid-cols-3 gap-2 overflow-visible rounded-none border-b border-border/50 bg-transparent p-0 sm:[grid-template-columns:repeat(var(--company-count),minmax(0,1fr))]"
                  style={{ '--company-count': companies.length } as CSSProperties}
                >
                  {companies.map(company => (
                    <TabsTrigger
                      key={company.key}
                      value={company.displayName}
                      className={cn(
                        'relative z-10 h-full min-w-0 whitespace-normal px-2 py-1 leading-tight transition-all duration-300 ease-out',
                        'data-[state=active]:overflow-hidden',

                        'data-[state=inactive]:rounded-lg data-[state=inactive]:text-sm data-[state=inactive]:font-medium data-[state=inactive]:text-black data-[state=inactive]:hover:bg-muted/5 data-[state=inactive]:hover:text-foreground/80 dark:data-[state=inactive]:text-white',

                        'data-[state=active]:z-20 data-[state=active]:translate-y-[-2px]',
                        'data-[state=active]:rounded-lg',

                        'data-[state=active]:border-x data-[state=active]:border-t-2 data-[state=active]:border-amber-400/35 data-[state=active]:bg-gradient-to-br data-[state=active]:from-amber-300 data-[state=active]:via-yellow-400 data-[state=active]:to-amber-500 data-[state=active]:text-amber-950 data-[state=active]:shadow-lg data-[state=active]:shadow-amber-500/55',
                        'data-[state=active]:before:absolute data-[state=active]:before:inset-0 data-[state=active]:before:bg-gradient-to-br data-[state=active]:before:from-white/45 data-[state=active]:before:via-amber-300/25 data-[state=active]:before:to-transparent data-[state=active]:before:opacity-65',
                        'data-[state=active]:after:absolute data-[state=active]:after:inset-0 data-[state=active]:after:bg-gradient-to-tl data-[state=active]:after:from-yellow-300/30 data-[state=active]:after:via-transparent data-[state=active]:after:to-white/25 data-[state=active]:after:opacity-55',

                        'dark:data-[state=active]:border-amber-500/35 dark:data-[state=active]:from-amber-400 dark:data-[state=active]:via-yellow-500 dark:data-[state=active]:to-amber-600 dark:data-[state=active]:text-black dark:data-[state=active]:shadow-amber-500/55',

                        'data-[state=active]:text-sm data-[state=active]:font-bold',
                      )}
                    >
                      <span className="relative z-10 flex items-center justify-center">
                        <CompanyLogoImage companyKey={company.key} size={16} />
                        {company.displayName}
                      </span>
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>

              { }
              {companies
                .filter(company => company.displayName === selectedCompany)
                .map((company) => {
                  const modelDisplayNames = company.models.map(m =>
                    getModelDisplayName(m),
                  )
                  const companyStats = comparisonData[company.displayName] || {}
                  const winRateStats
                    = companyWinRateData[company.displayName] || []

                  return (
                    <div key={company.key} className="space-y-6">
                      { }
                      <Card className="rounded-t-none border-t-0 shadow-none">
                        <CardHeader>
                          <CardTitle className="text-left text-xl font-semibold">
                            {t('whatWeDo.playerRankingTitle')}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {winRateStats.length === 0 ? (
                            <div className="py-8 text-center">
                              <p className="text-muted-foreground">
                                {t('leaderboard.noDataAvailable')}
                              </p>
                            </div>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full border-collapse">
                                <thead>
                                  <tr className="border-b border-border">
                                    <th className="px-4 py-3 text-left text-base font-semibold text-foreground">
                                      {t('whatWeDo.aiModel')}
                                    </th>
                                    <th className="px-4 py-3 text-center text-base font-semibold text-foreground">
                                      {t('whatWeDo.winRate')}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {winRateStats.map(stat => (
                                    <tr
                                      key={stat.model}
                                      className="border-b border-border/50 transition-colors hover:bg-muted/50"
                                    >
                                      <td className="px-4 py-3 text-left font-medium text-foreground">
                                        <div className="flex items-center">
                                          <LogoImage modelName={stat.model} size={20} />
                                          {stat.model}
                                        </div>
                                      </td>
                                      <td className="px-4 py-3 text-center">
                                        <span className="text-foreground">
                                          {stat.winRate.toFixed(1)}%
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      { }
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-left text-xl font-semibold">
                            {t('leaderboard.modelPairsMatrix')}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="overflow-x-auto">
                            <table className="w-full table-fixed border-collapse">
                              <thead>
                                <tr className="border-b border-border">
                                  <th className="w-[200px] px-4 py-3 text-left text-base font-semibold text-foreground">
                                    {t('arena.modelAVsModelB')}
                                  </th>
                                  {modelDisplayNames.map(model => (
                                    <th
                                      key={model}
                                      className="w-[150px] px-4 py-3 text-center text-base font-semibold text-foreground"
                                    >
                                      <div className="flex flex-col items-center">
                                        <LogoImage modelName={model} size={16} />
                                        <span className="block break-words">
                                          {model}
                                        </span>
                                      </div>
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {modelDisplayNames.map(modelA => (
                                  <tr
                                    key={modelA}
                                    className="border-b border-border/50 transition-colors hover:bg-muted/50"
                                  >
                                    <td className="w-[200px] px-4 py-3 text-left font-medium text-foreground">
                                      <div className="flex items-center">
                                        <LogoImage modelName={modelA} size={20} />
                                        <span className="block break-words">
                                          {modelA}
                                        </span>
                                      </div>
                                    </td>
                                    {modelDisplayNames.map((modelB) => {
                                      const data = companyStats[modelA]?.[modelB]
                                      const hasData = data !== undefined
                                      const displayValue
                                        = !hasData || data.wins === -1
                                          ? '-'
                                          : `${data.wins}/${data.total}`

                                      return (
                                        <td
                                          key={modelB}
                                          className="w-[150px] px-4 py-3 text-center"
                                        >
                                          <span
                                            className={`${
                                              !hasData || data.wins === -1
                                                ? 'text-muted-foreground'
                                                : 'text-foreground'
                                            }`}
                                          >
                                            {displayValue}
                                          </span>
                                        </td>
                                      )
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          <div className="mt-6 space-y-3">
                            <div className="rounded-lg border border-muted bg-muted/30 p-4">
                              <div className="grid gap-2 text-sm text-muted-foreground">
                                <p>
                                  {t('arena.matrixDescription', { company: company.displayName },
                                  )}
                                </p>
                                <p>
                                  {t('arena.diagonalNote')}
                                </p>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )
                })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export { AnalysisReportSection }
